import { z } from "zod";
import {
	ApiKeySchema,
	AppFileSchema,
	AsdCameraConfigSchema,
	AsdConfigSchema,
	AsdDetectionResultSchema,
	AsdFaceSchema,
	AsdIntelligenceConfigSchema,
	AsdLayoutConfigSchema,
	AsdOverlaysAndTimingConfigSchema,
	AsdResolutionSchema,
	AsdResultSchema,
	AsdRootSchema,
	AsdSceneSchema,
	AsdVideoInfoSchema,
	CostBreakdownSchema,
	ITranscriptionSchema,
	JobSchema,
	type OrganizationBranding,
	OrganizationSchema,
	type PredefinedShorts,
	PredefinedShortsSchema,
	type PredictionSchema,
	PurchaseSchema,
	RenderHistorySchema,
	ShortSchema,
	SourceFilesSchema,
	UserSchema,
	VideoSchema,
	type VideoShortsOptions,
	VideoShortsOptionsSchema,
	WebhookSchema,
	type WordSchema,
} from "./database/zod";
import { ISizeSchema } from "./src/video-formats";

// import { zColor } from "@remotion/zod-types";
import type * as CSS from "csstype";

export const SITE_NAME = "app";
export const RAM = 2048;
export const DISK = 2048;
export const VIDEOS_FPS = 30;
export const TIMEOUT = 120;
export const PresetName = z.enum([
	"DEFAULT",
	"CJ GRADIENT",
	"RED SHADOW STEAM",
	"WHITE TYPEWRITER",
	"WHITE TYPEWRITER BG",
	"GRAPES",
	"VIRAL",
	"BLURRY",
	"FAST",
	"DEEP DIVER",
	"GLOW VIOLET",
	"70S RADIATION",
	"BILL GREEN",
	"ETHEREAL UNDERLINE",
]);
export type PresetNameType = `${z.infer<typeof PresetName>}`;
export const blendModes = z.enum([
	"normal",
	"multiply",
	"screen",
	"overlay",
	"darken",
	"lighten",
	"color-dodge",
	"color-burn",
	"hard-light",
	"soft-light",
	"difference",
	"exclusion",
	"hue",
	"saturation",
	"color",
	"luminosity",
]);

export enum CompositionName {
	ShortGenerator = "Short",
	RenderVideo = "RenderVideo",
	Studio = "Studio",
}

/**
 * Todo: not working at the moment
 */
export const VIDEOLENGTHEXTRA = 0; // time to add to short by seconds

export const DefaultWaterMarkOptions: WaterMarkSchemaType = {
	waterMarkUrl: "/assets/swiftiawatermark.png",
	blendMode: "normal",
	opacity: "0.9",
	width: "200px",
};
export const Presets: { [key in PresetNameType]: any } = {
    [PresetName.Values.DEFAULT]: {
        maxWordsInPage: 5,
        // Subtitles settings
        waterMark: DefaultWaterMarkOptions,
        style: [
            {
                property: "textShadow",
                value: "-3px 1px 8px rgba(0, 0, 0, 0.82)"
            },
            {
                property: "translate",
                value: "0em 0.25em",
                active: {
                    value: "0em 0em",
                    duration: 0.5
                }
            },
            {
                property: "opacity",
                value: "0",
                active: {
                    value: "1",
                    duration: 0.5
                }
            },
            {
                property: "color",
                value: "#ffffffed",
                active: {
                    value: "#00ffff",
                    duration: 0.3
                },
                past: {
                    value: "#ffffffed",
                    duration: 0.7
                }
            }
        ]
    },
    [PresetName.Values.GRAPES]: {
        maxWordsInPage: 5,
        waterMark: DefaultWaterMarkOptions,
        style: [
            {
                property: "textTransform",
                value: "uppercase"
            },
            {
                property: "opacity",
                value: "0.3",
                active: {
                    value: "1",
                }
            },
            {
                property: "backgroundColor",
                value: "transparent",
                active: {
                    value: "#2766a7",
                },
                past: {
                    value: "transparent",
                }
            }
        ],
    },
    [PresetName.Values["CJ GRADIENT"]]: {
		"maxWordsInPage": 3,
		"waterMark": DefaultWaterMarkOptions,
		"style": [
			{
				"property": "translate",
				"value": "0em -0.25em",
				"active": {
					"value": "0em 0em",
					"duration": 0.2
				}
			},
			{
				"property": "opacity",
				"value": "0",
				"active": {
					"value": "1",
					"duration": 0.5
				}
			},
			{
				"property": "color",
				"value": "#ffffff00",
				"active": {
					"value": "",
					"duration": 0.3
				},
				"past": {
					"value": "",
					"duration": 0.7
				}
			},
			{
				"property": "background",
				"value": "linear-gradient(0deg,rgba(6,147,227,1) 25%,rgb(155,81,224) 75%)",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "backgroundClip",
				"value": "text",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "top",
				"value": "-600px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "position",
				"value": "relative",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "filter",
				"value": "drop-shadow(-0.5px -0.5px 0px rgba(0,0,0, 0.5)) drop-shadow(2px 2px 1px rgba(0, 0, 0, 0.9)) drop-shadow(0px 0px 20px rgba(155, 81, 224, 0.5))",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontFamily",
				"value": "Nunito Sans",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontWeight",
				"value": "1000",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "WebkitBackgroundClip",
				"value": "text",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			}
		]
	},
    [PresetName.Values["RED SHADOW STEAM"]]: {
		"maxWordsInPage": 5,
		"waterMark":DefaultWaterMarkOptions,
		"style": [
			{
				"property": "color",
				"value": "white",
				"active": {
					"value": "#ff0000ff",
					"duration": 0.5
				},
				"past": {
					"value": "#ffffff7a",
					"duration": 1
				}
			},
			{
				"property": "textShadow",
				"value": "",
				"active": {
					"value": "0px -00px 35px red",
					"duration": 0.2
				},
				"past": {
					"value": "0px -40px 85px red",
					"duration": 2
				}
			}
		]
	},
    [PresetName.Values["WHITE TYPEWRITER"]]: {
		"maxWordsInPage": 5,
		"waterMark": DefaultWaterMarkOptions,
		"style": [
			{
				"property": "opacity",
				"value": "0",
				"active": {
					"value": "1",
					"duration": 0.2
				}
			},
			{
				"property": "color",
				"value": "#d6d6d6ff",
				"active": {
					"value": "#ffffffff",
					"duration": 0.2
				},
				"past": {
					"value": "#d6d6d6ff",
					"duration": 1
				}
			},
			{
				"property": "position",
				"value": "relative",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "bottom",
				"value": "500px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "overflow",
				"value": "hidden",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontFamily",
				"value": "Roboto Mono",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontWeight",
				"value": "800",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "whiteSpace",
				"value": "nowrap",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "maxWidth",
				"value": "0%",
				"active": {
					"value": "100%",
					"duration": 1
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRightColor",
				"value": "#fc9b1f80",
				"active": {
					"value": "#fc9b1fff",
					"duration": 0.2
				},
				"past": {
					"value": "#ffffff00",
					"duration": 0.5
				}
			},
			{
				"property": "borderRightStyle",
				"value": "solid",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRightWidth",
				"value": "0.15em",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "height",
				"value": "60px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "lineHeight",
				"value": "100%",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "textShadow",
				"value": "1px 1px 1px rgba(0, 0, 0, 0.9), -1px -1px 1px rgba(0, 0, 0, 0.9)",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			}
		]
	},
    [PresetName.Values["WHITE TYPEWRITER BG"]]: {
		"maxWordsInPage": 5,
		"waterMark": DefaultWaterMarkOptions,
		"style": [
			{
				"property": "opacity",
				"value": "0",
				"active": {
					"value": "1",
					"duration": 0.2
				}
			},
			{
				"property": "color",
				"value": "#d6d6d6ff",
				"active": {
					"value": "#ffffffff",
					"duration": 0.2
				},
				"past": {
					"value": "#d6d6d6ff",
					"duration": 1
				}
			},
			{
				"property": "position",
				"value": "relative",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "bottom",
				"value": "500px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "overflow",
				"value": "hidden",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontFamily",
				"value": "Roboto Mono",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontWeight",
				"value": "800",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "whiteSpace",
				"value": "nowrap",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "maxWidth",
				"value": "0%",
				"active": {
					"value": "100%",
					"duration": 1
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRightColor",
				"value": "#fc9b1f80",
				"active": {
					"value": "#fc9b1fff",
					"duration": 0.2
				},
				"past": {
					"value": "#ffffff00",
					"duration": 0.5
				}
			},
			{
				"property": "borderRightStyle",
				"value": "solid",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRightWidth",
				"value": "0.15em",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "height",
				"value": "60px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "lineHeight",
				"value": "100%",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRadius",
				"value": "15px 0px 0px 15px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "marginLeft",
				"value": "-15px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "paddingRight",
				"value": "20px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "backgroundColor",
				"value": "#1c1c1cff",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			}
		]
	},
    [PresetName.Values.VIRAL]: {
        maxWordsInPage: 5,
        waterMark: DefaultWaterMarkOptions,
        style: [
            {
                property: "position",
                value: "relative"
            },
            {
                property: "zIndex",
                value: "1",
                active: {
                    value: "2",
                },
                past: {
                    value: "1",
                }
            },
            {
                property: "textShadow",
                value: "-8px -8px 10px #000, 0 -8px 10px #000, 8px -8px 10px #000, 8px 0 10px #000, 8px 8px 10px #000, 0 8px 10px #000, -8px 8px 10px #000, -8px 0 10px #000",
            },
            {
                property: "transform",
                value: "scale(1)",
                active: {
                    value: "scale(1.3)",
                    duration: 0.15
                },
                past: {
                    value: "scale(1)",
                    duration: 0.15
                }
            },
            {
                property: "padding",
                value: "0 10px",
            },
            {
                property: "color",
                value: "#FFF",
                active: {
                    value: "#06d420",
                },
                past: {
                    value: "#FFF",
                }
            },
            {
                property: "transformOrigin",
                value: "center"
            }
        ],
    },
    [PresetName.Values.BLURRY]: {
        maxWordsInPage: 5,
        waterMark: DefaultWaterMarkOptions,
        style: [
            {
                property: "textShadow",
                value: "-3px 1px 8px rgba(0, 0, 0, 0.82)"
            },
            {
                property: "filter",
                value: "blur(10px)",
                active: {
                    value: "blur(0px)",
                    duration: 0.5
                }
            },
            {
                property: "opacity",
                value: "0",
                active: {
                    value: "1",
                    duration: 0.5
                }
            },
        ],
    },
    [PresetName.Values.FAST]: {
        maxWordsInPage: 5,
        waterMark: DefaultWaterMarkOptions,
        style: [
            {
                "property": "transform",
                "value": "translateY(30px)",
                "active": {
                    "value": "translateY(0px)",
                    "duration": 0.3
                }
            },
            {
                "property": "opacity",
                "value": "0",
                "active": {
                    "value": "1",
                    "duration": 0.3
                }
            },
            {
                "property": "textShadow",
                "value": "0em 0em 0px #c9c9c980, 0em 0em 0px #8f8f8f61, 0em 0em 0px #9696963d, 0em 0em 0px #ededed24",
                "active": {
                    "value": "0 0.03125em 10px #c9c9c980, 0 0.0825em 10px #8f8f8f61, 0 0.13125em 10px #9696963d, 0 0.22425em 10px #ededed24",
                    "duration": 0.7
                },
                "past": {
                    "value": "0 0.03125em 10px #c9c9c980, 0 0.0825em 10px #8f8f8f61, 0 0.13125em 10px #9696963d, 0 0.22425em 10px #ededed24",
                    "duration": 0.3
                }
            }
        ],
    },
    [PresetName.Values["DEEP DIVER"]]:{
        "maxWordsInPage": 3,
        "waterMark": DefaultWaterMarkOptions,
        "style": [
            {
                "property": "textShadow",
                "value": "0",
                "active": {
                    "value": "-3px 1px 8px rgba(0, 0, 0, 0.82)"
                }
            },
            {
                "property": "opacity",
                "value": "1",
                "active": {
                    "value": "1",
                    "duration": 0.5
                }
            },
            {
                "property": "color",
                "value": "#ffffff54",
                "active": {
                    "value": "#ffffffff",
                    "duration": 0.3
                },
                "past": {
                    "value": "#ffffffcf",
                    "duration": 0.5
                }
            },
            {
                "property": "backgroundColor",
                "value": "#417505ff",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "padding",
                "value": "0 10px"
            },
            {
                "property": "margin",
                "value": "0 -5px",
            },
            {
                "property": "borderRadius",
                "value": "10px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "fontSize",
                "value": "48px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "fontFamily",
                "value": "Roboto",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "fontWeight",
                "value": "800",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            }
        ]
    },
    [PresetName.Values["GLOW VIOLET"]]:{
		"maxWordsInPage": 5,
        waterMark: DefaultWaterMarkOptions,
		"style": [
			{
				"property": "color",
				"value": "#fbf9f9ff",
				"active": {
					"value": "#d410d0ff",
					"duration": 0
				},
				"past": {
					"value": "#e0e0e0ff",
					"duration": 0
				}
			},
			{
				"property": "textShadow",
				"value": "0 0 5px #fff, 0 0 10px #ff00ff, 0 0 15px #ff00ff",
				"active": {
					"value": "0 0 10px #fff, 0 0 20px #ff00ff, 0 0 30px #ff00ff",
					"duration": 0
				},
				"past": {
					"value": "0 0 5px #fff, 0 0 10px #ff00ff, 0 0 15px #ff00ff",
					"duration": 0
				}
			},
			{
				"property": "fontFamily",
				"value": "Abril Fatface",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "fontWeight",
				"value": "800",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			}
		]
	},
    [PresetName.Values["70S RADIATION"]]:{
		"maxWordsInPage": 5,
		"waterMark": DefaultWaterMarkOptions,
		"style": [
			{
				"property": "textShadow",
				"value": "-3px -3px 0 rgba(0, 255, 255, 0.8),       3px 3px 0 rgba(255, 0, 255, 0.8)",
				"active": {
					"value": "4px -4px 0 rgba(255, 0, 0, 0.7),       -4px 4px 0 rgba(0, 255, 0, 0.7)",
					"duration": 0
				},
				"past": {
					"value": "-2px 2px 0 rgba(0, 255, 255, 0.9),       2px -2px 0 rgba(255, 255, 0, 0.9)",
					"duration": 0
				}
			},
			{
				"property": "color",
				"value": "",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "transform",
				"value": "skew(-5deg)        scale(1.02)        translate(-3px, 2px)",
				"active": {
					"value": "skew(3deg)        scale(0.98)        translate(3px, -2px)",
					"duration": 0
				},
				"past": {
					"value": "skew(1deg)        scale(1.01)        translate(0, 0)",
					"duration": 0
				}
			},
			{
				"property": "backgroundColor",
				"value": "#1f0f0fff",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "padding",
				"value": "0px 10px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			},
			{
				"property": "borderRadius",
				"value": "10px",
				"active": {
					"value": "",
					"duration": 0
				},
				"past": {
					"value": "",
					"duration": 0
				}
			}
		]
	},
    [PresetName.Values["BILL GREEN"]]:{
        "maxWordsInPage": 2,
        "waterMark": DefaultWaterMarkOptions,
        "style": [
            {
                "property": "textShadow",
                "value": "-3px 1px 8px rgba(0, 0, 0, 0.82)"
            },
            {
                "property": "translate",
                "value": "0em -400px",
                "active": {
                    "value": "",
                    "duration": 0.5
                }
            },
            {
                "property": "opacity",
                "value": "0",
                "active": {
                    "value": "1",
                    "duration": 0.5
                }
            },
            {
                "property": "color",
                "value": "#ffffffed",
                "active": {
                    "value": "#00ffff",
                    "duration": 0.3
                },
                "past": {
                    "value": "#ffffffed",
                    "duration": 0.7
                }
            },
            {
                "property": "fontFamily",
                "value": "Arimo",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "color",
                "value": "#dce067ff",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "backgroundColor",
                "value": "#000000ff",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "padding",
                "value": "00px 20px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "borderRadius",
                "value": "10px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            }
        ]
    },
    [PresetName.Values["ETHEREAL UNDERLINE"]]:{
        "maxWordsInPage": 5,
        "waterMark": DefaultWaterMarkOptions,
        "style": [
            {
                "property": "textShadow",
                "value": "-3px 1px 8px rgba(0, 0, 0, 0.82)"
            },
            {
                "property": "opacity",
                "value": "0",
                "active": {
                    "value": "1",
                    "duration": 0.5
                }
            },
            {
                "property": "fontFamily",
                "value": "Lato",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "fontWeight",
                "value": "800",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "borderBottom",
                "value": "4mm solid rgba(211, 220, 50, .6)",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "borderRadius",
                "value": "5px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "padding",
                "value": "0px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "margin",
                "value": "10px 15px",
                "active": {
                    "value": "",
                    "duration": 0
                },
                "past": {
                    "value": "",
                    "duration": 0
                }
            },
            {
                "property": "transform",
                "value": "scale(0.9)",
                "active": {
                    "value": "scale(1.2)",
                    "duration": 0.2
                },
                "past": {
                    "value": "scale(1)",
                    "duration": 0.1
                }
            },
            {
                "property": "lineHeight",
                "value": "110%",
                "active": {
                    "value": "70%",
                    "duration": 0.2
                },
                "past": {
                    "value": "100%",
                    "duration": 0.1
                }
            }
        ]
    }
}

export const ClipPhaseAnimationOptions = z
	.object({
		easing: z.string(),
		extrapolateLeft: z.string(),
		extrapolateRight: z.string(),
	})
	.optional();
export type ClipPhaseAnimationOptionsType = z.infer<
	typeof ClipPhaseAnimationOptions
>;

export type ClipOptionsStyleProperty = keyof CSS.Properties<string | number>;

export const ClipOptionsStylePhaseSchema = z.object({
    value: z.string(),
    duration: z.number().optional(),
    options: ClipPhaseAnimationOptions.optional()
}).optional()

export type ClipOptionsStylePhaseSchemaType = z.infer<typeof ClipOptionsStylePhaseSchema>

export const ClipOptionsStyleSchema = z.object({
    property: z.string(),
    value: z.string(),
    active: ClipOptionsStylePhaseSchema.optional(),
    past: ClipOptionsStylePhaseSchema.optional()
})
export type ClipOptionsStyleSchemaType = z.infer<typeof ClipOptionsStyleSchema>

export const CaptionWordEffectStateSchema = z.object({
	enabled: z.boolean(),
	intensity: z.number().min(0).max(2).optional(),
});
export type CaptionWordEffectStateType = z.infer<
	typeof CaptionWordEffectStateSchema
>;

export const CaptionWordEffectsSchema = z.object({
	jellyWobble: CaptionWordEffectStateSchema.optional(),
});
export type CaptionWordEffectsType = z.infer<typeof CaptionWordEffectsSchema>;

// Phase detection enum
export enum Phase {
	INITIAL = "initial",
	ACTIVE = "active",
	PAST = "past",
}

export const WaterMarkSchema = z.object({
	waterMarkUrl: z.string(),
	width: z.string().optional(),
	height: z.string().optional(),
	top: z.string().optional(),
	left: z.string().optional(),
	opacity: z.string().optional(),
	blendMode: blendModes.default("normal").optional(),
});
export type WaterMarkSchemaType = z.infer<typeof WaterMarkSchema>;

export const EditedContentSchema = z.object({
	words: z.array(
		z.object({
			predictionIndex: z.number(),
			wordIndex: z.number(),
			content: z.string(),
		}),
	),
});
export type EditedContentSchemaType = z.infer<typeof EditedContentSchema>;


export const AsdConfigWithRelationsSchema = AsdConfigSchema.merge(
	z.object({
		camera: z.lazy(() => AsdCameraConfigWithRelationsSchema).optional(),
		layout: z.lazy(() => AsdLayoutConfigWithRelationsSchema).optional(),
		intelligence: z.lazy(() => AsdIntelligenceConfigWithRelationsSchema).optional(),
		overlaysAndTiming: z.lazy(() => AsdOverlaysAndTimingConfigWithRelationsSchema).optional(),
	}),
);
export type AsdConfigWithRelations = z.infer<typeof AsdConfigWithRelationsSchema>;

// Updated to use object-based style structure
export const clipOptionsSchema = z.object({
    style: z.array(ClipOptionsStyleSchema).optional(),
	contentOptions: EditedContentSchema.optional(),
	waterMark: WaterMarkSchema.optional(),
	maxWordsInPage: z.number().optional(),
	wordEffects: CaptionWordEffectsSchema.optional(),
	director: AsdConfigWithRelationsSchema.optional(),
});
export type ClipOptionsSchemaType = z.infer<typeof clipOptionsSchema>;

export const VideoWithRelationsSchema = VideoSchema.merge(
	z.object({
		job: z.lazy(() => JobSchema),
		files: z.lazy(() => SourceFilesSchema).nullable(),
		options: z.lazy(() => VideoShortsOptionsSchema).nullable(),
		transcription: z.lazy(() => ITranscriptionSchema).nullable(),
		shorts: z.lazy(() => ShortSchema).array(),
	}),
);

export type VideoWithRelations = z.infer<typeof VideoWithRelationsSchema>;

export const JobWithRelationsSchema = JobSchema.merge(
	z.object({
		user: z.lazy(() => UserSchema),
		organization: z.lazy(() => OrganizationSchema),
		key: z.lazy(() => ApiKeySchema).nullable(),
		purchase: z.lazy(() => PurchaseSchema).nullable(),
		video: z.lazy(() => VideoSchema).nullable(),
		files: z.lazy(() => AppFileSchema).array(),
		renderHistories: z.lazy(() => RenderHistorySchema).array(),
		webhook: z.lazy(() => WebhookSchema).nullable(),
		costBreakDown: z.lazy(() => CostBreakdownSchema).nullable(),
	}),
);

export type JobWithRelations = z.infer<typeof JobWithRelationsSchema>;

// default Compo props, Short Generator
export const CompositionVideoShortsProps = z.object({
	video: VideoWithRelationsSchema,
	/** Present when video.id was stripped from the job payload (e.g. mapVideoShortJob). */
	videoId: z.string().optional(),
	compSettingChanged: z.function().optional(),
	target: z.number(),
	preset: PresetName.default(PresetName.Values.DEFAULT).optional(),
	options: clipOptionsSchema,
	isLambda: z.boolean().default(false).optional(),
	isStylePreview: z.boolean().default(false).optional(),
	resolution: z.object({
		width: z.number(),
		height: z.number(),
	}),
});
export type CompositionVideoShortsPropsType = z.infer<typeof CompositionVideoShortsProps>;


export const ShortVideoRenderRequestSchema = z.object({
	id: z.string(),
	target: z.number(),
	options: clipOptionsSchema.optional(),
	size: ISizeSchema.optional(),
	/** Explicit saved organization brand preset to apply for this render. */
	brandPresetId: z.string().optional(),
	preset: PresetName.optional(),
});
export type ShortVideoRenderRequest = z.infer<typeof ShortVideoRenderRequestSchema>;


export const RenderResponseSchema = z.object({
	renderId: z.string(),
	bucketName: z.string(),
	cloudWatchLogs: z.string().url(),
	cloudWatchMainLogs: z.string().url(),
	folderInS3Console: z.string().url(),
	lambdaInsightsLogs: z.string().url(),
	progressJsonInConsole: z.string().url(),
});
export type RenderResponse = z.infer<typeof RenderResponseSchema>;


export const ProgressRequestSchema = z.object({
	id: z.string(),
});

export type ProgressRequest = z.infer<typeof ProgressRequestSchema>;

export type ProgressResponse =
	| {
			type: "error";
			message: string;
	  }
	| {
			type: "progress";
			progress: number;
	  }
	| {
			type: "done";
			url: string;
			size: number;
	  };

export type State =
	| {
			status: "init";
	  }
	| {
			status: "invoking";
	  }
	| {
			renderId: string;
			progress: number;
			status: "rendering";
	  }
	| {
			renderId: string | null;
			status: "error";
			error: Error;
	  }
	| {
			url: string;
			size: number;
			status: "done";
	  };

export type TTSCustomRequest = {
	id: string;
	content: string;
	// language: StoryLanguagesType;
	tone?: string;
	voiceName?: string;
	emotion?: string;
};

export type lambdaChatGPTTTSPayload = {
	jobId: string;
	TTSRequests: TTSCustomRequest[];
};

export type TTSCutomResponse = {
	ttsFile: string;
	id: string;
	// language: StoryLanguagesType;
};

export type lambdaChatGPTTTSOutput = {
	jobId: string;
	data: TTSCutomResponse[];
};

export type lambdaShortGenerationFinalPayload = {
	jobId: string;
	data: TTSCutomResponse[];
};

export type lambdaShortGenerationPayload = {
	jobId: string;
	sourceId: string;
	retryCount?: number;
};

export enum LambdaVideoProcessorType {
	CONVERT_TO_AUDIO = "CONVERT_TO_AUDIO",
	GET_VIDEO_DETAILS = "GET_VIDEO_DETAILS",
	GET_VIDEO_DURATION = "GET_VIDEO_DURATION",
	GET_VIDEO_FILE_SIZE = "GET_VIDEO_FILE_SIZE",
	GET_VIDEO_RESOLUTION = "GET_VIDEO_RESOLUTION",
	DOWNLOAD_PARTIAL_VIDEO = "DOWNLOAD_PARTIAL_VIDEO",
	TRIM_VIDEO = "TRIM_VIDEO",
	GENERATE_THUMBNAIL = "GENERATE_THUMBNAIL",
	/** Last video frame → JPEG on S3 (plate lastFrameUrl + AppFile thumbnail). */
	EXTRACT_LAST_FRAME = "EXTRACT_LAST_FRAME",
}

export interface LambdaGenerateThumbnail {
	type: "GENERATE_THUMBNAIL";
	input: string;
	outputKey: string;
	retryCount?: number;
}

export interface LambdaExtractLastFrame {
	type: "EXTRACT_LAST_FRAME";
	input: string;
	outputKey: string;
	retryCount?: number;
}

export type LambdaVideoProcessor = {
	type: LambdaVideoProcessorType;
	input: string;
	retryCount?: number;
};

export type LambdaPartialVideoDownload = {
	type: "DOWNLOAD_PARTIAL_VIDEO";
	input: string;
	startTime?: number;
	endTime?: number;
	retryCount?: number;
};

export type LambdaTrimVideo = {
	type: "TRIM_VIDEO";
	input: string;
	startTime: number;
	endTime: number;
	outputKey: string;
	retryCount?: number;
};

export type Author = {
	id: string;
	name: string;
	user: string;
	channel_url: string;
	external_channel_url: string;
	user_url: string;
	thumbnails: Thumbnail[];
	verified: boolean;
	subscriber_count: number;
};

export type Thumbnail = {
	url: string;
	width: number;
	height: number;
};

export type Stats = {
	embed: Embed;
	description: string;
	lengthSeconds: string;
	ownerProfileUrl: string;
	externalChannelId: string;
	isFamilySafe: boolean;
	availableCountries: string[];
	isUnlisted: boolean;
	hasYpcMetadata: boolean;
	viewCount: string;
	category: string;
	publishDate: string;
	ownerChannelName: string;
	uploadDate: string;
	isShortsEligible: boolean;
	videoId: string;
	keywords: string[];
	channelId: string;
	isOwnerViewing: boolean;
	isCrawlable: boolean;
	allowRatings: boolean;
	isPrivate: boolean;
	isUnpluggedCorpus: boolean;
	isLiveContent: boolean;
	media: string;
	likes: string;
	dislikes: string;
	age_restricted: boolean;
	video_url: string;
	storyboards: Storyboard[];
	chapters: string[];
	thumbnails: Thumbnail2[];
};

export type Embed = {
	iframeUrl: string;
	width: number;
	height: number;
};

export type Storyboard = {
	templateUrl: string;
	thumbnailWidth: number;
	thumbnailHeight: number;
	thumbnailCount: number;
	interval: number;
	columns: number;
	rows: number;
	storyboardCount: number;
};

export type Thumbnail2 = {
	url: string;
	width: number;
	height: number;
};

export type YoutubeVideoInfo = {
	id: string;
	title: string;
	description: string;
	publishedAt: string;
	duration: string;
	thumbnail: {
		url: string;
		width: number;
		height: number;
	};
	viewCount: number;
	likeCount: number;
	dislikeCount: number;
	commentCount: number;
};

export enum AspectRatio {
	ULTRA_WIDE_SCREEN = "ULTRA_WIDE_SCREEN",
	CLASSIC_TV = "CLASSIC_TV",
	SMART_PHONES = "SMART_PHONES",
	WIDE_SCREEN_169 = "WIDE_SCREEN_169",
	WIDE_SCREEN_1610 = "WIDE_SCREEN_1610",
	CINEMASCOPE = "CINEMASCOPE",
}

export const AspectRatioValues = {
	[AspectRatio.CINEMASCOPE]: { width: 2.35, height: 1 },
	[AspectRatio.WIDE_SCREEN_1610]: { width: 16, height: 10 },
	[AspectRatio.WIDE_SCREEN_169]: { width: 16, height: 9 },
	[AspectRatio.SMART_PHONES]: { width: 9, height: 16 },
	[AspectRatio.CLASSIC_TV]: { width: 4, height: 3 },
	[AspectRatio.ULTRA_WIDE_SCREEN]: { width: 21, height: 9 },
};

export const PlansMetaData = z.object({
	unit: z.string(),
	hourPrice: z.string(),
	maxApiKeys: z.string(),
	maxTeamsMembers: z.string(),
	resolution: z.string(),
	storage: z.string(),
	storageDuration: z.string(),
	maxFileUpload: z.string(),
	isFree: z.string().optional(),
});
export type PlansMetDataType = z.infer<typeof PlansMetaData>;

export const AppConfigsSchema = z.object({
	activePlan: PlansMetaData,
	ytshort: z.object({
		maxInputDuration: z.number(),
		minimumShortDuration: z.number(),
		maximumShortDuration: z.number(),
		minimumDefinedShortDuration: z.number(),
		maximumDefinedShortDuration: z.number(),
		minimumDefinedShortPrice: z.number(),
		youtubeDownloadPricePerGB: z.number(),
		minimumYoutubeProcessingPrice: z.number(),
		youtubeDownloadMethod: z.enum(["n8n", "sieve"]),
	}),
	credit: z.object({
		teamCredit: z.number(),
		lockedCredit: z.number(),
	}),
	publicEnv: z.object({
		bucketName: z.string(),
	}),
});
export type AppConfigs = z.infer<typeof AppConfigsSchema>;

// StoryGenerator Compo props, Short Generator
export const StoryCompositionProps = z.object({
	trackItemIds: z.array(z.any()),
	trackItemsMap: z.any(),
	fps: z.number(),
	trackItemDetailsMap: z.any(),
	size: z.any(),
	transitionsMap: z.any(),
	duration: z.number(),
});

export const ChatGPTVoices = [
	{
		name: "Alloy",
		gender: "male",
	},
	{
		name: "Ash",
		gender: "male",
	},
	{
		name: "Ballad",
		gender: "male",
	},
	{
		name: "Coral",
		gender: "female",
	},
	{
		name: "Ecko",
		gender: "male",
	},
	{
		name: "Fable",
		gender: "male",
	},
	{
		name: "Onyx",
		gender: "male",
	},
	{
		name: "Nova",
		gender: "female",
	},
	{
		name: "Sage",
		gender: "female",
	},
	{
		name: "Shimmer",
		gender: "female",
	},
	{
		name: "Verse",
		gender: "male",
	},
];

export const ApiKeyWithRelationsSchema = ApiKeySchema.merge(
	z.object({
		user: z.lazy(() => UserSchema),
	}),
);

export type ApiKeyWithRelations = z.infer<typeof ApiKeyWithRelationsSchema>;

export const ShortWithRelationsSchema = ShortSchema.merge(
	z.object({
		asd: z.lazy(() => AsdRootWithRelationsSchema).optional().nullable(),
	}),
);
export type ShortWithRelations = z.infer<typeof ShortWithRelationsSchema>;

// WithRelations types
export type PredictionWithRelations = z.infer<typeof PredictionSchema> & {
	words: z.infer<typeof WordSchema>[];
};
export type ITranscriptionWithRelations = z.infer<
	typeof ITranscriptionSchema
> & { prediction: PredictionWithRelations[] };

/**
 * Interface for use in lambdas and AI providers for transcription results.
 * Mirrors the structure of ITranscriptionWithRelations but as an interface.
 */
export interface ITranscriptionRelations {
	prediction: PredictionWithRelations[];
}

export interface VideoShortsOptionsWithRelations extends VideoShortsOptions {
	predefinedShorts?: PredefinedShorts[];
}

export const VideoShortsOptionsWithRelationsSchema =
	VideoShortsOptionsSchema.merge(
		z.object({
			predefinedShorts: z
				.lazy(() => PredefinedShortsSchema)
				.array()
				.optional(),
		}),
	);

// ASD Composite Types with Relations
export const AsdResolutionWithRelationsSchema = AsdResolutionSchema.merge(
	z.object({}),
);
export type AsdResolutionWithRelations = z.infer<typeof AsdResolutionWithRelationsSchema>;

export const AsdVideoInfoWithRelationsSchema = AsdVideoInfoSchema.merge(
	z.object({
		resolution: z.lazy(() => AsdResolutionWithRelationsSchema),
	}),
);
export type AsdVideoInfoWithRelations = z.infer<typeof AsdVideoInfoWithRelationsSchema>;

export const AsdFaceWithRelationsSchema = AsdFaceSchema.merge(z.object({}));
export type AsdFaceWithRelations = z.infer<typeof AsdFaceWithRelationsSchema>;

export const AsdDetectionResultWithRelationsSchema =
	AsdDetectionResultSchema.merge(
		z.object({
			faces: z.lazy(() => AsdFaceWithRelationsSchema).array(),
		}),
	);
export type AsdDetectionResultWithRelations = z.infer<typeof AsdDetectionResultWithRelationsSchema>;

export const AsdSceneWithRelationsSchema = AsdSceneSchema.merge(z.object({}));
export type AsdSceneWithRelations = z.infer<typeof AsdSceneWithRelationsSchema>;

export const AsdResultWithRelationsSchema = AsdResultSchema.merge(
	z.object({
		detection_results: z.lazy(() => AsdDetectionResultWithRelationsSchema).array(),
		video_info: z.lazy(() => AsdVideoInfoWithRelationsSchema),
		scenes: z.lazy(() => AsdSceneWithRelationsSchema).array(),
	}),
);
export type AsdResultWithRelations = z.infer<typeof AsdResultWithRelationsSchema>;

export const AsdCameraConfigWithRelationsSchema = AsdCameraConfigSchema.merge(z.object({}));
export type AsdCameraConfigWithRelations = z.infer<typeof AsdCameraConfigWithRelationsSchema>;

export const AsdLayoutConfigWithRelationsSchema = AsdLayoutConfigSchema.merge(z.object({}));
export type AsdLayoutConfigWithRelations = z.infer<typeof AsdLayoutConfigWithRelationsSchema>;

export const AsdIntelligenceConfigWithRelationsSchema = AsdIntelligenceConfigSchema.merge(z.object({}));
export type AsdIntelligenceConfigWithRelations = z.infer<typeof AsdIntelligenceConfigWithRelationsSchema>;

export const AsdOverlaysAndTimingConfigWithRelationsSchema = AsdOverlaysAndTimingConfigSchema.merge(z.object({}));
export type AsdOverlaysAndTimingConfigWithRelations = z.infer<typeof AsdOverlaysAndTimingConfigWithRelationsSchema>;


export const AsdRootWithRelationsSchema = AsdRootSchema.merge(
	z.object({
		result: z.lazy(() => AsdResultWithRelationsSchema),
		video_info: z.lazy(() => AsdVideoInfoWithRelationsSchema),
		config: z.lazy(() => AsdConfigWithRelationsSchema).optional(),
	}),
);
export type AsdRootWithRelations = z.infer<typeof AsdRootWithRelationsSchema>;

export enum appErrorsKeys {
	notAuthorized = "notAuthorized",
	functionNotFound = "functionNotFound",
	youtubeVideoIdIsNotSet = "youtubeVideoIdIsNotSet",
	videoNotFound = "videoNotFound",
	invalidPredefinedShortsLength = "invalidPredefinedShortsLength",
	maxVideoLength = "maxVideoLength",
	alreadyMember = "alreadyMember",
	alreadyInvited = "alreadyInvited",
	NoEnoughCredit = "noEnoughCredit",
	maxKeyReached = "maxKeyReached",
	maxMembersReached = "maxMembersReached",
	fileOverlimitedSize = "fileOverlimitedSize",
	invalidFileType = "invalidFileType",
	fileTypeMismatch = "fileTypeMismatch",
	unsupportedFileFormat = "unsupportedFileFormat",
	fullStorage = "fullStorage",
	apiKeyNameExist = "apiKeyNameExist",
}

export interface StylePhasesType {
	[property: string]: {
		initial?: ClipOptionsStylePhaseSchemaType;
		active?: ClipOptionsStylePhaseSchemaType;
		past?: ClipOptionsStylePhaseSchemaType;
	};
}


export interface BillingPeriod {
	start: Date;
	end: Date;
	isTrial?: boolean;
	recurringPeriod?: "month" | "year" | "week";
}


export interface JobWithCostBreakdown {
	costBreakDown?: {
		shortGen?: number;
		youtubeProcessing?: number;
		render?: number;
	};
}

/**
 * Defines the input and output video resolutions.
 */
export interface VideoResolutions {
	input: {
	  width: number;
	  height: number;
	};
	output: {
	  width: number;
	  height: number;
	};
  }
  
  /**
   * Represents a rectangular area for cropping or positioning.
   */
  export interface Rect {
	x: number;
	y: number;
	width: number;
	height: number;
  }
  
  /**
   * Represents a bounding box for visualization/debugging.
   */
  export interface BoundingBox {
	x: number;
	y: number;
	width: number;
	height: number;
	color?: string; // Optional color for the bounding box
	speakingScore?: number; // Speaking score for display
  }
  
  /**
   * Describes the layout for a single video source within the output frame.
   */
  export interface Layout {
	sourceCrop: Rect;
	destination: Rect;
  }
  
  /**
   * An indicator representing a continuous time segment with a specific layout.
   */
  export interface Indicator {
	startFrame: number;
	endFrame: number;
	layouts: Layout[];
	trackId?: number; // For single speaker tracking
	layoutKey: string; // To identify the type of layout (e.g., "1", "1-2")
  }
  


export interface AppOrganizationBranding extends Omit<OrganizationBranding, "options"> {
	options: BrandingPresetOptions;
}
export interface BrandingPresetOptions {
	maxWordsInPage?: number;
	wordEffects?: CaptionWordEffectsType;
	waterMark?: {
		/** Studio / org file; resolved to a URL at render time when possible. */
		fileAppId?: string;
		/** Direct URL (e.g. API-created presets); used when file lookup is missing or fails. */
		waterMarkUrl?: string;
		left?: string | null;
		top?: string | null;
		blendMode?: string;
		opacity?: string;
		width?: string;
	};
	style?: Array<{
		property: string;
		value: string;
		active?: { value: string; duration: number };
		past?: { value: string; duration: number };
	}>;
	director?: {
		camera?: Record<string, any>;
		layout?: Record<string, any>;
		intelligence?: Record<string, any>;
		overlaysAndTiming?: Record<string, any>;
	};
	/** Project-video beat on-screen text placement (ignored for shorts). */
	onScreenTextLayout?: {
		left?: string | null;
		right?: string | null;
		top?: string | null;
		width?: string | null;
	};
	/** @deprecated Prefer onScreenTextLayout left/top (LTR) or right/top (RTL). */
	onScreenTextPosition?:
		| "top-left"
		| "top-center"
		| "top-right"
		| "middle-left"
		| "middle-center"
		| "middle-right"
		| "bottom-left"
		| "bottom-center"
		| "bottom-right";
	/**
	 * Narrator profile overlay text placement (video projects).
	 * Independent from beat on-screen text layout.
	 */
	narratorTextLayout?: {
		left?: string | null;
		right?: string | null;
		top?: string | null;
		width?: string | null;
	};
	/** Narrator avatar PIP size and position (video projects). */
	narratorAvatar?: {
		left?: string | null;
		top?: string | null;
		width?: string | null;
		height?: string | null;
		/** Corner roundness, same 0–100 scale as the editor Round control. */
		round?: number | null;
		/** @deprecated Prefer left/top/width. */
		widthRatio?: number;
		/** @deprecated Prefer left/top/width. */
		marginRatio?: number;
		/** @deprecated Prefer left/top/width. */
		corner?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
	};
}